// ============================================================
// Libellés et tons des opérateurs (CE-1404) : statuts, régimes
// d'accompagnement, état des calendriers d'une fiche. Le mot « CER » est
// proscrit des écrans opérateurs : on dit « contrat d'engagement ».
// ============================================================

import { formaterMesures } from "./quotasOperateur";

export const STATUTS_OPERATEUR = {
  ACTIF: { label: "Actif", tone: "neutral", icon: "fa-check" },
  EN_PREPARATION: { label: "En préparation", tone: "warn", icon: "fa-tools" },
  EN_SERVICE: { label: "En service", tone: "ok", icon: "fa-check-circle" },
  INACTIF: { label: "Inactif", tone: "muted", icon: "fa-pause-circle" },
};

export const libelleStatut = (statut) =>
  (STATUTS_OPERATEUR[statut] || {}).label || statut || "";

// Textes des cartes de régime, repris de la maquette 10 validée par la Caf.
export const REGIMES_ACCOMPAGNEMENT = {
  TOTAL: {
    label: "Total",
    titre: "Accompagnement total",
    sousTitre: "délégation",
    description: "L'opérateur devient référent unique, en remplacement du référent Caf.",
    points: [
      "Rendez-vous, contrat, contradictoire, EP, réorientation",
      "Calendriers de rendez-vous requis",
      "Absences comptées comme à la Caf",
    ],
  },
  PARTIEL: {
    label: "Partiel",
    titre: "Accompagnement partiel",
    sousTitre: "un volet",
    description: "Le travailleur social Caf reste référent ; l'opérateur intervient sur un volet.",
    points: [
      "Voit l'identité, la fiche, le RDO, l'existence d'un contrat",
      "Pointe ses rendez-vous",
      "Absences hors calcul automatique",
    ],
  },
};

export const libelleRegime = (regime) =>
  (REGIMES_ACCOMPAGNEMENT[regime] || {}).label || regime || "";

export const ALERTES_SITE = {
  SANS_BOX: "Aucun box de réception",
  SANS_CRENEAU: "Aucun créneau ouvert",
};

// Étapes de l'encart « Mise en service » d'une fiche en préparation.
// `etat` : ok | ko (bloquant) | partiel (signalé sans bloquer) | avenir.
// Côté API, l'absence de site et l'absence d'administrateur activé bloquent
// la mise en service ; les créneaux et les quotas de l'année sont signalés
// sans bloquer. `comptes` :
// les comptes de la structure (CE-1407), null tant qu'ils ne sont pas chargés
// (l'étape reste « à venir » et l'API tranche).
export function etapesMiseEnService(fiche, comptes = null) {
  const etapes = [{ etat: "ok", texte: "Identité et régimes renseignés" }];
  const nbSites = Number(fiche.nombreSites) || 0;
  etapes.push(
    nbSites
      ? { etat: "ok", texte: nbSites > 1 ? `${nbSites} sites d'accueil` : "1 site d'accueil" }
      : { etat: "ko", texte: "Aucun site d'accueil", action: { onglet: "sites", libelle: "Ajouter un site" } }
  );
  const calendriers = fiche.calendriers || {};
  const avec = Number(calendriers.sitesAvecCreneaux) || 0;
  const sans = Number(calendriers.sitesSansCreneaux) || 0;
  if (!nbSites) {
    etapes.push({ etat: "partiel", texte: "Créneaux ouverts sur au moins un site" });
  } else if (!avec) {
    etapes.push({
      etat: "partiel",
      texte: "Aucun créneau ouvert",
      action: { onglet: "sites", libelle: "Ouvrir un calendrier" },
    });
  } else if (sans) {
    etapes.push({
      etat: "partiel",
      texte: `Créneaux ouverts sur ${avec} site${avec > 1 ? "s" : ""} sur ${avec + sans}`,
      action: { onglet: "sites", libelle: "Voir" },
    });
  } else {
    etapes.push({ etat: "ok", texte: avec > 1 ? "Créneaux ouverts sur tous les sites" : "Créneaux ouverts" });
  }
  etapes.push(etapeAdministrateur(comptes));
  const quotas = etapeQuotas(fiche.quotas);
  if (quotas) etapes.push(quotas);
  return etapes;
}

// Les quotas de l'année en cours (CE-1369) : leur absence est signalée, elle
// ne bloque pas la mise en service. Avec des quotas, l'étape dit ce qu'il
// reste (mesures utilisées : CE-1370, zéro d'ici là) ; un quota atteint est
// signalé sans bloquer. Une fiche sans la clé n'affiche rien.
function etapeQuotas(quotas) {
  if (!quotas) return null;
  if (quotas.total > 0) {
    const utilisees = quotas.utilisees || 0;
    const restantes = quotas.total - utilisees;
    const voir = { onglet: "quotas", libelle: "Voir" };
    if (restantes > 0) {
      const pluriel = restantes > 1 ? "s" : "";
      return { etat: "ok", texte: `Quotas ${quotas.annee} : ${restantes.toLocaleString("fr-FR")} mesure${pluriel} restante${pluriel} sur ${quotas.total}`, action: voir };
    }
    return { etat: "partiel", texte: `Quotas ${quotas.annee} : quota atteint, ${formaterMesures(utilisees)} sur ${quotas.total}`, action: voir };
  }
  return {
    etat: "partiel",
    texte: `Aucun quota pour ${quotas.annee}`,
    action: { onglet: "quotas", libelle: "Saisir les quotas" },
  };
}

const estAdministrateur = (c) => c && c.role === "ADMIN_OPERATEUR";

function etapeAdministrateur(comptes) {
  if (!Array.isArray(comptes)) return { etat: "avenir", texte: "Un administrateur activé" };
  const actives = comptes.filter((c) => estAdministrateur(c) && c.estActive).length;
  if (actives) {
    return { etat: "ok", texte: actives > 1 ? `${actives} administrateurs activés` : "1 administrateur activé" };
  }
  const enAttente = comptes.some((c) => estAdministrateur(c) && !c.estActive && !c.activeLe);
  return {
    etat: "ko",
    texte: enAttente ? "Administrateur en attente d'activation" : "Aucun administrateur activé",
    action: { onglet: "utilisateurs", libelle: "Ajouter un utilisateur" },
  };
}

export const miseEnServicePossible = (fiche, comptes = null) =>
  etapesMiseEnService(fiche, comptes).every((e) => e.etat !== "ko");

// État des calendriers d'une fiche { nombreSites, regimes, statut,
// calendriers: { sitesAvecCreneaux, sitesSansCreneaux } }.
// null quand l'information n'a pas de sens (opérateur classique ou inactif).
export function etatCalendriers(operateur) {
  if (!operateur) return null;
  const regimes = operateur.regimes || [];
  if (!regimes.length || operateur.statut === "INACTIF") return null;
  const calendriers = operateur.calendriers || {};
  const avec = Number(calendriers.sitesAvecCreneaux) || 0;
  const sans = Number(calendriers.sitesSansCreneaux) || 0;
  if (avec + sans === 0) {
    return { tone: "warn", icon: "fa-exclamation-triangle", texte: "Aucun site" };
  }
  if (sans === 0) {
    return { tone: "ok", icon: "fa-check", texte: avec > 1 ? "Tous les sites" : "Ouvert" };
  }
  if (avec === 0) {
    return { tone: "warn", icon: "fa-exclamation-triangle", texte: "Aucun créneau" };
  }
  return {
    tone: "warn",
    icon: "fa-exclamation-triangle",
    texte: `${sans} site${sans > 1 ? "s" : ""} sans créneau`,
  };
}
